import React, { useState, useMemo } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { ZipcodeRecord, LocationLabCourtRecord, LookupResult, ZoneBadge, getZoneStyle } from '../data/location-shared';

// ── Types ──

interface ZoneLookupTabProps {
  brand: BrandConfig;
  zipcodes: ZipcodeRecord[];
  courts: LocationLabCourtRecord[];
}

// ── Component ──

const ZoneLookupTab: React.FC<ZoneLookupTabProps> = ({ brand, zipcodes, courts }) => {
  const [lookupQuery, setLookupQuery] = useState('');

  // ── Client-side lookup (pure derivation from query + data) ──

  const { lookupResults, lookupTotal } = useMemo(() => {
    if (!lookupQuery.trim()) {
      return { lookupResults: [] as LookupResult[], lookupTotal: 0 };
    }

    const q = lookupQuery.trim().toLowerCase();
    const isZip = /^\d{3,5}$/.test(q);

    let matched: ZipcodeRecord[];
    if (isZip) {
      const zipNum = parseInt(q, 10);
      matched = zipcodes.filter(r => r['Zip Code'] === zipNum);
    } else {
      matched = zipcodes.filter(
        r => r.City.toLowerCase().includes(q) || r['City Alias Name'].toLowerCase().includes(q),
      );
    }

    // Deduplicate
    const seen = new Set<string>();
    const deduped: LookupResult[] = [];

    for (const rec of matched) {
      const key = `${rec.City}|${rec['Zip Code']}|${rec['Pricing Zone']}`;
      if (seen.has(key)) {
        const existing = deduped.find(
          d => d.city === rec.City && d.zipCode === rec['Zip Code'] && d.zone === rec['Pricing Zone'],
        );
        if (existing && !existing.aliases.includes(rec['City Alias Name'])) {
          existing.aliases.push(rec['City Alias Name']);
        }
        continue;
      }
      seen.add(key);

      const countyCourts = courts.filter(ct => ct.County === rec.County);
      const courtZones = [...new Set(countyCourts.map(ct => ct['Pricing Zone']))];

      deduped.push({
        city: rec.City,
        zipCode: rec['Zip Code'],
        county: rec.County,
        zone: rec['Pricing Zone'],
        areaName: rec.Name,
        aliases: [rec['City Alias Name']],
        courtZone: courtZones.length > 0 ? courtZones.join(', ') : null,
      });
    }

    return { lookupResults: deduped.slice(0, 100), lookupTotal: deduped.length };
  }, [lookupQuery, zipcodes, courts]);

  // ── Render ──

  return (
    <div>
      {/* Search Input */}
      <div className="max-w-xl mb-6">
        <label htmlFor="zone-lookup-query" className="block text-sm font-medium mb-2" style={{ color: brand.colors.text }}>
          Enter a city name or zip code
        </label>
        <input
          id="zone-lookup-query"
          type="text"
          value={lookupQuery}
          onChange={(e) => setLookupQuery(e.target.value)}
          placeholder="e.g., 90210 or San Francisco"
          className="w-full px-4 py-3 text-lg rounded-lg border"
          style={{
            borderColor: brand.colors.border,
            color: brand.colors.text,
            backgroundColor: 'white',
          }}
        />
        {lookupQuery && (
          <div className="mt-1 text-sm" style={{ color: brand.colors.textMuted }}>
            {lookupTotal === 0 ? 'No matches found' : `${lookupTotal} match${lookupTotal !== 1 ? 'es' : ''} found`}
          </div>
        )}
      </div>

      {/* Results */}
      {lookupResults.length > 0 && (
        <div className="space-y-4">
          {lookupResults.map((r) => {
            const zoneStyle = getZoneStyle(r.zone);
            return (
              <div
                key={`${r.city}-${r.zipCode}-${r.zone}`}
                className="rounded-lg border p-4"
                style={{ borderColor: brand.colors.border, borderLeftWidth: 4, borderLeftColor: zoneStyle.fill }}
              >
                {/* Narrative */}
                <p className="text-sm mb-3" style={{ color: brand.colors.text }}>
                  An order to <strong>{r.city}</strong>, <strong>{String(r.zipCode).padStart(5, '0')}</strong> in{' '}
                  <strong>{r.county}</strong> County would be classified as{' '}
                  <ZoneBadge zone={r.zone} />.
                </p>

                {/* Details */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-xs" style={{ color: brand.colors.textMuted }}>Area Name</div>
                    <div style={{ color: brand.colors.text }}>{r.areaName}</div>
                  </div>
                  <div>
                    <div className="text-xs" style={{ color: brand.colors.textMuted }}>County</div>
                    <div style={{ color: brand.colors.text }}>{r.county}</div>
                  </div>
                  <div>
                    <div className="text-xs" style={{ color: brand.colors.textMuted }}>Court Zone</div>
                    <div style={{ color: brand.colors.text }}>{r.courtZone ?? 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-xs" style={{ color: brand.colors.textMuted }}>Aliases</div>
                    <div style={{ color: brand.colors.textLight }}>
                      {r.aliases.filter(a => a !== r.city).length > 0
                        ? r.aliases.filter(a => a !== r.city).join(', ')
                        : 'None'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {!lookupQuery && (
        <div
          className="text-center py-12 rounded-lg border"
          style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgLight }}
        >
          <div className="text-4xl mb-3" style={{ color: brand.colors.textMuted }}>
            ?
          </div>
          <p className="text-sm" style={{ color: brand.colors.textLight }}>
            Type a city name or zip code above to look up its pricing zone classification.
          </p>
          <div className="mt-4 flex gap-3 justify-center">
            {['90210', 'San Francisco', '96162'].map(example => (
              <button
                key={example}
                onClick={() => setLookupQuery(example)}
                className="px-3 py-1 text-sm rounded border"
                style={{ borderColor: brand.colors.border, color: brand.colors.link }}
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ZoneLookupTab;
